import { decodeEntities } from './utils';

/**
 * Where each sentence ends.
 *
 * Shared because two surfaces cut copy at sentence boundaries: the Read more
 * crop, and the product statement's step down to smaller type past its first
 * few lines. One pattern, so they agree on what a sentence is.
 */
const SENTENCE_PATTERN = /[^.!?]*[.!?]+(?=\s|$)|[^.!?]+$/g;

/**
 * End offsets of every complete sentence, plus the end of the text so copy that
 * never terminates still has a boundary to cut at.
 */
export const sentenceEndOffsets = (value: string): number[] => {
    const offsets: number[] = [];
    for (const match of value.matchAll(SENTENCE_PATTERN)) {
        const end = (match.index ?? 0) + match[0].length;
        if (end > 0) offsets.push(end);
    }
    if (!offsets.length || offsets[offsets.length - 1] !== value.length) {
        offsets.push(value.length);
    }
    return offsets;
};

/**
 * CMS markup as text, with the block breaks kept.
 *
 * Block ends become newlines so a description written as several paragraphs
 * still reads as several paragraphs once it is text.
 */
export const htmlToPlainText = (html: string): string => {
    const withBreaks = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
        .replace(/<[^>]*>/g, '');

    return decodeEntities(withBreaks)
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

/**
 * CMS markup as the text of a line-clamped teaser, rendered with
 * `whitespace-pre-line`. Line breaks in the markup's source are only
 * formatting, so they collapse; each block break becomes a single newline. A
 * description that opens on a title line keeps it on its own line, rather than
 * running it into the sentence after, without spending a clamped line on a
 * blank one.
 */
export const htmlToTeaserText = (html: string): string =>
    htmlToPlainText(html.replace(/\s+/g, ' ')).replace(/\s*\n\s*/g, '\n');
